"use client";

import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import Link from "next/link";
import { ArrowLeft, Minus, Plus, Square, Volume2 } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";

/** Text-size steps, as a multiplier on the reading column's base size. */
const SCALES = [0.85, 1, 1.15, 1.3, 1.45];
const SCALE_KEY = "lh.readerScale";
const subscribeNever = () => () => {};

function readStoredScale(): number {
  try {
    const n = Number(localStorage.getItem(SCALE_KEY));
    return SCALES.includes(n) ? n : 1;
  } catch {
    return 1;
  }
}

/**
 * The reader's tools on an article, in a strip that sticks under the site
 * header while reading: back to the list, the text size, read aloud.
 *
 * - Text size sets `--reader-scale` on the root element (the reading column's
 *   `data-reader-text` region sizes its type from it), remembered per browser
 *   in localStorage. The first client render applies the stored value; the
 *   server renders 100 %, so a returning reader sees one reflow — acceptable
 *   for a per-device preference, and no layout shift where it matters (the
 *   hero is above, and unscaled).
 * - Read aloud uses the browser's own speech synthesis on the article's text,
 *   in the page's language. Progressive: the button renders only where the
 *   API exists, and a voice for the language is whatever the device has.
 * - The strip's height is published as `--reader-tools-h` so the floating
 *   right unit and the article's sidebar start below it instead of under it,
 *   and a contents link lands a subhead below it (`scroll-margin-top`).
 * - Touch: every control is a 44 pt target on coarse pointers (Apple HIG);
 *   the strip pads for the phone's safe areas.
 */
export function ReaderTools({
  backHref,
  backLabel,
  actions,
  className,
}: {
  backHref: string;
  /** The back link's text — the news label by default; the post article
   *  passes its own (D35: one strip for every article). */
  backLabel?: string;
  /** Save and share, from the article. They live HERE rather than in the row
   *  under the lede: that row scrolls away, so a reader who finished the piece
   *  had to scroll back up to act on it. Icon buttons only — the strip is a
   *  row of icons. */
  actions?: ReactNode;
  className?: string;
}) {
  const t = useDict().news;
  const stored = useSyncExternalStore(subscribeNever, readStoredScale, () => 1);
  const [scale, setScale] = useState<number | null>(null);
  const current = scale ?? stored;
  const idx = SCALES.indexOf(current);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--reader-scale", String(current));
    try {
      localStorage.setItem(SCALE_KEY, String(current));
    } catch {
      // A blocked store just means the size is not remembered.
    }
    return () => {
      root.style.removeProperty("--reader-scale");
    };
  }, [current]);

  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return;
    const publish = () => root.style.setProperty("--reader-tools-h", `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--reader-tools-h");
    };
  }, []);

  // Speech: available only where the browser has it; stopped on unmount and
  // on navigation, so a reader never leaves a page still talking.
  const canSpeak = useSyncExternalStore(
    subscribeNever,
    () => typeof window !== "undefined" && "speechSynthesis" in window,
    () => false,
  );
  const [speaking, setSpeaking] = useState(false);
  useEffect(
    () => () => {
      window.speechSynthesis?.cancel();
    },
    [],
  );
  const toggleSpeech = () => {
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    // The region minus what is not prose — the inline contents list carries
    // `data-reader-skip`; a listener wants the article, not its index.
    const region = document.querySelector<HTMLElement>("[data-reader-text]");
    const copy = region?.cloneNode(true) as HTMLElement | undefined;
    copy?.querySelectorAll("[data-reader-skip]").forEach((el) => el.remove());
    const text = copy?.innerText;
    if (!text) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = document.documentElement.lang || "sl-SI";
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    synth.cancel();
    synth.speak(u);
    setSpeaking(true);
  };

  const iconBtn =
    "text-muted-foreground hover:text-foreground focus-visible:ring-ring inline-flex size-8 items-center justify-center rounded-md transition-colors pointer-coarse:size-11 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:hover:text-muted-foreground";

  return (
    <div ref={ref} className={cn("flex items-center justify-between gap-3", className)}>
      {/* The words go below `sm`: with save and share now in the strip, a
          phone truncated this to a single letter ("N…"), which says less than
          the arrow alone. The label stays as the accessible name. */}
      <Link
        href={backHref}
        aria-label={backLabel ?? t.backToList}
        className="text-muted-foreground hover:text-foreground inline-flex min-w-0 items-center gap-1.5 text-sm font-semibold transition-colors"
      >
        <ArrowLeft className="size-4 shrink-0" aria-hidden />
        <span className="hidden truncate sm:inline">{backLabel ?? t.backToList}</span>
      </Link>
      <div className="flex shrink-0 items-center gap-2">
        <div
          role="group"
          aria-label={t.textSize}
          className="bg-muted flex items-center rounded-lg p-0.5"
        >
          <button
            type="button"
            title={t.textSmaller}
            aria-label={t.textSmaller}
            disabled={idx <= 0}
            onClick={() => setScale(SCALES[Math.max(0, idx - 1)] ?? 1)}
            className={cn(iconBtn, "hover:bg-card")}
          >
            <Minus className="size-3.5" aria-hidden />
          </button>
          <span
            aria-live="polite"
            className="text-muted-foreground min-w-[3rem] text-center text-xs font-medium tabular-nums"
          >
            {Math.round(current * 100)} %
          </span>
          <button
            type="button"
            title={t.textLarger}
            aria-label={t.textLarger}
            disabled={idx >= SCALES.length - 1}
            onClick={() => setScale(SCALES[Math.min(SCALES.length - 1, idx + 1)] ?? 1)}
            className={cn(iconBtn, "hover:bg-card")}
          >
            <Plus className="size-3.5" aria-hidden />
          </button>
        </div>
        {canSpeak ? (
          <button
            type="button"
            title={speaking ? t.stopReading : t.readAloud}
            aria-label={speaking ? t.stopReading : t.readAloud}
            aria-pressed={speaking}
            onClick={toggleSpeech}
            className={cn(
              iconBtn,
              "bg-muted hover:bg-border/60 size-9 pointer-coarse:size-11",
              speaking && "text-foreground",
            )}
          >
            {speaking ? (
              <Square className="size-3.5" aria-hidden />
            ) : (
              <Volume2 className="size-4" aria-hidden />
            )}
          </button>
        ) : null}
        {actions}
      </div>
    </div>
  );
}
